import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { achievementsAPI, designerAPI } from '../lib/api';
import { 
  Lightbulb, 
  Trophy, 
  BookOpen, 
  Zap, 
  Clock, 
  Flag, 
  Users, 
  Star,
  Plus,
  Calendar,
  User,
  Trash2,
  Edit2,
  X,
  Filter,
  Sparkles,
  Presentation,
  Upload,
  Image as ImageIcon,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  RotateCcw,
  MessageSquare,
  Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { formatDate, cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  innovation: Lightbulb,
  award: Trophy,
  learning: BookOpen,
  kt_session: Presentation,
  automation: Zap,
  early_delivery: Clock,
  milestone: Flag,
  collaboration: Users,
  other: Star
};

const CATEGORY_COLORS = {
  innovation: { bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-600' },
  award: { bg: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-600' },
  learning: { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-600' },
  kt_session: { bg: 'bg-indigo-500', light: 'bg-indigo-100', text: 'text-indigo-600' },
  automation: { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-600' },
  early_delivery: { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-600' },
  milestone: { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-600' },
  collaboration: { bg: 'bg-cyan-500', light: 'bg-cyan-100', text: 'text-cyan-600' },
  other: { bg: 'bg-slate-500', light: 'bg-slate-100', text: 'text-slate-600' }
};

const STATUS_CONFIG = {
  pending: { label: 'Pending Approval', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgLight: 'bg-yellow-100', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-100', icon: CheckCircle },
  needs_revision: { label: 'Needs Revision', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-100', icon: AlertCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-100', icon: XCircle }
};

const Achievements = () => {
  const { user, isArtist, isManager } = useAuth();
  const { isDark } = useTheme();
  const [achievements, setAchievements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDesigner, setSelectedDesigner] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [uploadingScreenshot, setUploadingScreenshot] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [reviewingAchievement, setReviewingAchievement] = useState(null);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [resubmitting, setResubmitting] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    impact: '',
    date_achieved: ''
  });

  const fetchAchievements = useCallback(async () => {
    try {
      const params = {};
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedDesigner !== 'all') params.designer_id = selectedDesigner;
      
      const response = await achievementsAPI.getAll(params);
      setAchievements(response.data);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedDesigner]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes] = await Promise.all([
          achievementsAPI.getCategories()
        ]);
        setCategories(categoriesRes.data);
        
        if (!isArtist()) {
          const designersRes = await designerAPI.getAllIncludingInactive();
          setDesigners(designersRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      fetchAchievements();
    };
    fetchData();
  }, [isArtist, fetchAchievements]);

  useEffect(() => {
    fetchAchievements();
  }, [selectedCategory, selectedDesigner, fetchAchievements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingId) {
        await achievementsAPI.update(editingId, formData);
        toast.success('Achievement updated successfully');
      } else {
        await achievementsAPI.create(formData);
        toast.success('Achievement added successfully!');
      }
      
      setShowForm(false);
      setEditingId(null);
      setFormData({ title: '', category: '', description: '', impact: '', date_achieved: '' });
      fetchAchievements();
    } catch (error) {
      console.error('Error saving achievement:', error);
      toast.error(error.response?.data?.detail || 'Failed to save achievement');
    }
  };

  const handleEdit = (achievement) => {
    setFormData({
      title: achievement.title,
      category: achievement.category,
      description: achievement.description,
      impact: achievement.impact || '',
      date_achieved: achievement.date_achieved || ''
    });
    setEditingId(achievement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this achievement?')) return;
    
    try {
      await achievementsAPI.delete(id);
      toast.success('Achievement deleted');
      fetchAchievements();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete achievement');
    }
  };

  const handleScreenshotUpload = async (achievementId, file) => {
    if (!file) return;
    
    setUploadingScreenshot(achievementId);
    try {
      await achievementsAPI.uploadScreenshot(achievementId, file);
      toast.success('Screenshot uploaded');
      fetchAchievements();
    } catch (error) {
      console.error('Error uploading screenshot:', error);
      toast.error('Failed to upload screenshot');
    } finally {
      setUploadingScreenshot(null);
    }
  };

  const handleDeleteScreenshot = async (achievementId, screenshotId) => {
    if (!window.confirm('Delete this screenshot?')) return;
    
    try {
      await achievementsAPI.deleteScreenshot(achievementId, screenshotId);
      toast.success('Screenshot deleted');
      fetchAchievements();
    } catch (error) {
      console.error('Error deleting screenshot:', error);
      toast.error('Failed to delete screenshot');
    }
  };

  // Approval workflow handlers
  const handleApprove = async (achievementId) => {
    try {
      await achievementsAPI.approve(achievementId, reviewFeedback || null);
      toast.success('Achievement approved!');
      setReviewingAchievement(null);
      setReviewFeedback('');
      fetchAchievements();
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Failed to approve');
    }
  };

  const handleRequestRevision = async (achievementId) => {
    if (!reviewFeedback.trim()) {
      toast.error('Please provide feedback for revision');
      return;
    }
    try {
      await achievementsAPI.requestRevision(achievementId, reviewFeedback);
      toast.success('Revision requested');
      setReviewingAchievement(null);
      setReviewFeedback('');
      fetchAchievements();
    } catch (error) {
      console.error('Error requesting revision:', error);
      toast.error('Failed to request revision');
    }
  };

  const handleReject = async (achievementId) => {
    if (!reviewFeedback.trim()) {
      toast.error('Please provide reason for rejection');
      return;
    }
    try {
      await achievementsAPI.reject(achievementId, reviewFeedback);
      toast.success('Achievement rejected');
      setReviewingAchievement(null);
      setReviewFeedback('');
      fetchAchievements();
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('Failed to reject');
    }
  };

  const handleResubmit = async (achievement) => {
    try {
      await achievementsAPI.resubmit(achievement.id, {
        title: achievement.title,
        category: achievement.category,
        description: achievement.description,
        impact: achievement.impact || '',
        date_achieved: achievement.date_achieved || ''
      });
      toast.success('Achievement resubmitted for review');
      setResubmitting(null);
      fetchAchievements();
    } catch (error) {
      console.error('Error resubmitting:', error);
      toast.error('Failed to resubmit');
    }
  };

  const getImageUrl = (filename) => {
    return `${BACKEND_URL}/api/screenshots/file/${filename}`;
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat?.label || value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="achievements-page">
      <Toaster position="top-right" richColors />
      
      {/* Image Preview Modal */}
      <AnimatePresence>
        {previewImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setPreviewImage(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setPreviewImage(null)}
                className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={getImageUrl(previewImage.filename)}
                alt={previewImage.original_filename}
                className="max-w-full max-h-[80vh] mx-auto rounded-lg"
              />
              <p className="text-white text-center mt-2 text-sm">{previewImage.original_filename}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Action Required Section - For Managers */}
      {isManager() && achievements.filter(a => a.status === 'pending').length > 0 && (
        <Card className={cn(
          "border-2 border-orange-400 bg-gradient-to-r",
          isDark ? "from-orange-900/30 to-slate-800" : "from-orange-50 to-white"
        )}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className={cn(
                "flex items-center gap-2 text-lg",
                isDark ? "text-orange-400" : "text-orange-600"
              )}>
                <AlertCircle className="w-5 h-5" />
                Action Required - Pending Approvals
              </CardTitle>
              <span className={cn(
                "px-3 py-1 rounded-full text-sm font-bold",
                isDark ? "bg-orange-500 text-white" : "bg-orange-500 text-white"
              )}>
                {achievements.filter(a => a.status === 'pending').length} Pending
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {achievements.filter(a => a.status === 'pending').slice(0, 5).map((achievement) => {
                const Icon = CATEGORY_ICONS[achievement.category] || Star;
                const colors = CATEGORY_COLORS[achievement.category] || CATEGORY_COLORS.other;
                
                return (
                  <div 
                    key={achievement.id}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg",
                      isDark ? "bg-slate-700/50 hover:bg-slate-700" : "bg-white hover:bg-slate-50 shadow-sm"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", colors.bg)}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className={cn("font-medium text-sm", isDark ? "text-white" : "text-slate-800")}>
                          {achievement.title}
                        </p>
                        <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                          by {achievement.designer_name} • {getCategoryLabel(achievement.category)}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        setReviewingAchievement(achievement);
                        setReviewFeedback('');
                      }}
                      className="bg-[#331062] hover:bg-[#2A0D52]"
                    >
                      <MessageSquare className="w-3 h-3 mr-1" />
                      Review
                    </Button>
                  </div>
                );
              })}
              {achievements.filter(a => a.status === 'pending').length > 5 && (
                <p className={cn("text-center text-sm pt-2", isDark ? "text-slate-400" : "text-slate-500")}>
                  +{achievements.filter(a => a.status === 'pending').length - 5} more pending...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Designer's Pending Feedback Section */}
      {isArtist() && achievements.filter(a => a.status === 'needs_revision').length > 0 && (
        <Card className={cn(
          "border-2 border-orange-400 bg-gradient-to-r",
          isDark ? "from-orange-900/30 to-slate-800" : "from-orange-50 to-white"
        )}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className={cn(
                "flex items-center gap-2 text-lg",
                isDark ? "text-orange-400" : "text-orange-600"
              )}>
                <AlertCircle className="w-5 h-5" />
                Action Required - Revisions Needed
              </CardTitle>
              <span className={cn(
                "px-3 py-1 rounded-full text-sm font-bold bg-orange-500 text-white"
              )}>
                {achievements.filter(a => a.status === 'needs_revision').length} Need Revision
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {achievements.filter(a => a.status === 'needs_revision').map((achievement) => (
                <div 
                  key={achievement.id}
                  className={cn(
                    "p-3 rounded-lg",
                    isDark ? "bg-slate-700/50" : "bg-white shadow-sm"
                  )}
                >
                  <p className={cn("font-medium text-sm", isDark ? "text-white" : "text-slate-800")}>
                    {achievement.title}
                  </p>
                  <p className={cn("text-xs mt-1 p-2 rounded bg-orange-100 dark:bg-orange-900/30", isDark ? "text-orange-300" : "text-orange-700")}>
                    💬 {achievement.manager_feedback}
                  </p>
                  <Button
                    size="sm"
                    onClick={() => handleResubmit(achievement)}
                    className="mt-2 bg-[#331062] hover:bg-[#2A0D52]"
                  >
                    <Send className="w-3 h-3 mr-1" />
                    Resubmit
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={cn(
            "text-2xl md:text-3xl font-bold font-outfit flex items-center gap-3",
            isDark ? "text-white" : "text-slate-800"
          )}>
            <Sparkles className="w-8 h-8 text-[#CCFF00]" />
            Achievements & Innovations
          </h1>
          <p className={cn("mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            Celebrate your wins, innovations, and milestones
          </p>
        </div>
        <Button
          onClick={() => {
            setShowForm(true);
            setEditingId(null);
            setFormData({ title: '', category: '', description: '', impact: '', date_achieved: '' });
          }}
          className="bg-[#331062] hover:bg-[#2A0D52]"
          data-testid="add-achievement-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Achievement
        </Button>
      </div>

      {/* Filters */}
      <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex items-center gap-2 flex-1">
              <Filter className={cn("w-4 h-4", isDark ? "text-slate-400" : "text-slate-500")} />
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isArtist() && (
              <Select value={selectedDesigner} onValueChange={setSelectedDesigner}>
                <SelectTrigger className="w-48" data-testid="designer-filter">
                  <SelectValue placeholder="All Team Members" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Team Members</SelectItem>
                  {designers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="needs_revision">Needs Revision</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Review Modal */}
      <AnimatePresence>
        {reviewingAchievement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setReviewingAchievement(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={cn(
                "w-full max-w-md rounded-xl p-6",
                isDark ? "bg-slate-800" : "bg-white"
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className={cn("text-lg font-bold", isDark ? "text-white" : "text-slate-800")}>
                  Review Achievement
                </h2>
                <button onClick={() => setReviewingAchievement(null)}>
                  <X className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                </button>
              </div>
              
              <div className={cn("p-3 rounded-lg mb-4", isDark ? "bg-slate-700" : "bg-slate-50")}>
                <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                  {reviewingAchievement.title}
                </p>
                <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
                  by {reviewingAchievement.designer_name}
                </p>
              </div>

              <div className="mb-4">
                <Label>Feedback / Comments (required for revision or rejection)</Label>
                <Textarea
                  value={reviewFeedback}
                  onChange={(e) => setReviewFeedback(e.target.value)}
                  placeholder="Enter your feedback here..."
                  rows={3}
                  className={isDark ? "bg-slate-700 border-slate-600" : ""}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleApprove(reviewingAchievement.id)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button
                  onClick={() => handleRequestRevision(reviewingAchievement.id)}
                  variant="outline"
                  className="flex-1 border-orange-500 text-orange-500 hover:bg-orange-50"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Revise
                </Button>
                <Button
                  onClick={() => handleReject(reviewingAchievement.id)}
                  variant="outline"
                  className="flex-1 border-red-500 text-red-500 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={cn(
                "w-full max-w-lg rounded-xl p-6",
                isDark ? "bg-slate-800" : "bg-white"
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className={cn(
                  "text-xl font-bold font-outfit",
                  isDark ? "text-white" : "text-slate-800"
                )}>
                  {editingId ? 'Edit Achievement' : 'Add New Achievement'}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className={cn(
                    "p-2 rounded-full",
                    isDark ? "hover:bg-slate-700" : "hover:bg-slate-100"
                  )}
                >
                  <X className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Automated Weekly Report Generation"
                    data-testid="achievement-title-input"
                    className={isDark ? "bg-slate-700 border-slate-600" : ""}
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="achievement-category-select" className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            {React.createElement(CATEGORY_ICONS[cat.value] || Star, { className: "w-4 h-4" })}
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what you accomplished..."
                    rows={3}
                    data-testid="achievement-description-input"
                    className={isDark ? "bg-slate-700 border-slate-600" : ""}
                  />
                </div>

                <div>
                  <Label htmlFor="impact">Business Impact (Optional)</Label>
                  <Textarea
                    id="impact"
                    value={formData.impact}
                    onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                    placeholder="e.g., Saved 5 hours per week, Increased efficiency by 30%"
                    rows={2}
                    data-testid="achievement-impact-input"
                    className={isDark ? "bg-slate-700 border-slate-600" : ""}
                  />
                </div>

                <div>
                  <Label htmlFor="date">Date Achieved (Optional)</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date_achieved}
                    onChange={(e) => setFormData({ ...formData, date_achieved: e.target.value })}
                    data-testid="achievement-date-input"
                    className={isDark ? "bg-slate-700 border-slate-600" : ""}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#331062] hover:bg-[#2A0D52]"
                    data-testid="save-achievement-btn"
                  >
                    {editingId ? 'Update' : 'Add Achievement'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AnimatePresence>
          {achievements.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Sparkles className={cn("w-12 h-12 mx-auto mb-4", isDark ? "text-slate-600" : "text-slate-300")} />
              <h3 className={cn("text-lg font-medium", isDark ? "text-white" : "text-slate-800")}>
                No achievements yet
              </h3>
              <p className={isDark ? "text-slate-400" : "text-slate-500"}>
                Start by adding your first achievement!
              </p>
            </div>
          ) : (
            achievements.filter(a => selectedStatus === 'all' || a.status === selectedStatus).map((achievement, index) => {
              const Icon = CATEGORY_ICONS[achievement.category] || Star;
              const colors = CATEGORY_COLORS[achievement.category] || CATEGORY_COLORS.other;
              const isOwner = achievement.user_id === user?.id;
              const status = STATUS_CONFIG[achievement.status] || STATUS_CONFIG.pending;
              const StatusIcon = status.icon;

              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className={cn(
                      "overflow-hidden h-full",
                      isDark ? "bg-slate-800 border-slate-700" : ""
                    )}
                    data-testid={`achievement-${achievement.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", colors.bg)}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className={cn(
                              "text-xs font-medium px-2 py-1 rounded-full",
                              isDark ? "bg-slate-700 text-slate-300" : colors.light + " " + colors.text
                            )}>
                              {getCategoryLabel(achievement.category)}
                            </span>
                            {/* Status Badge */}
                            <span className={cn(
                              "text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 w-fit",
                              isDark ? "bg-slate-700" : status.bgLight,
                              status.textColor
                            )}>
                              <StatusIcon className="w-3 h-3" />
                              {status.label}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {/* Manager Review Button */}
                          {isManager() && achievement.status === 'pending' && (
                            <button
                              onClick={() => {
                                setReviewingAchievement(achievement);
                                setReviewFeedback('');
                              }}
                              className="p-2 rounded-lg bg-[#331062] text-white hover:bg-[#2A0D52] transition-colors"
                              title="Review"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          )}
                          {(isOwner || isManager()) && (
                            <>
                              <button
                                onClick={() => handleEdit(achievement)}
                                className={cn(
                                  "p-2 rounded-lg transition-colors",
                                  isDark ? "hover:bg-slate-700" : "hover:bg-slate-100"
                                )}
                                data-testid={`edit-achievement-${achievement.id}`}
                              >
                                <Edit2 className={cn("w-4 h-4", isDark ? "text-slate-400" : "text-slate-500")} />
                              </button>
                              <button
                                onClick={() => handleDelete(achievement.id)}
                                className={cn(
                                  "p-2 rounded-lg transition-colors hover:bg-red-100",
                                  isDark ? "hover:bg-red-900/30" : ""
                                )}
                                data-testid={`delete-achievement-${achievement.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <h3 className={cn(
                        "font-semibold text-lg mb-2",
                        isDark ? "text-white" : "text-slate-800"
                      )}>
                        {achievement.title}
                      </h3>
                      <p className={cn(
                        "text-sm mb-3",
                        isDark ? "text-slate-300" : "text-slate-600"
                      )}>
                        {achievement.description}
                      </p>

                      {/* Manager Feedback Display */}
                      {achievement.manager_feedback && (
                        <div className={cn(
                          "p-3 rounded-lg mb-3 border-l-4",
                          achievement.status === 'approved' ? "border-l-green-500 bg-green-50 dark:bg-green-900/20" :
                          achievement.status === 'needs_revision' ? "border-l-orange-500 bg-orange-50 dark:bg-orange-900/20" :
                          "border-l-red-500 bg-red-50 dark:bg-red-900/20"
                        )}>
                          <p className={cn("text-xs font-medium mb-1", isDark ? "text-slate-400" : "text-slate-500")}>
                            Feedback from {achievement.reviewed_by_name}:
                          </p>
                          <p className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-700")}>
                            {achievement.manager_feedback}
                          </p>
                        </div>
                      )}

                      {/* Resubmit Button for Designer */}
                      {isOwner && (achievement.status === 'needs_revision' || achievement.status === 'rejected') && (
                        <Button
                          onClick={() => handleResubmit(achievement)}
                          size="sm"
                          className="mb-3 bg-[#331062] hover:bg-[#2A0D52]"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Resubmit for Review
                        </Button>
                      )}
                      
                      {achievement.impact && (
                        <div className={cn(
                          "p-3 rounded-lg mb-3",
                          isDark ? "bg-slate-700/50" : "bg-green-50"
                        )}>
                          <p className={cn(
                            "text-sm font-medium",
                            isDark ? "text-green-400" : "text-green-700"
                          )}>
                            💡 Impact: {achievement.impact}
                          </p>
                        </div>
                      )}

                      {/* Screenshots Section */}
                      {achievement.screenshots && achievement.screenshots.length > 0 && (
                        <div className="mb-3">
                          <p className={cn("text-xs font-medium mb-2", isDark ? "text-slate-400" : "text-slate-500")}>
                            Screenshots ({achievement.screenshots.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {achievement.screenshots.map((screenshot) => (
                              <div key={screenshot.id} className="relative group">
                                <img
                                  src={getImageUrl(screenshot.filename)}
                                  alt={screenshot.original_filename}
                                  className="w-16 h-16 object-cover rounded-lg cursor-pointer border border-slate-200 dark:border-slate-600"
                                  onClick={() => setPreviewImage(screenshot)}
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => setPreviewImage(screenshot)}
                                    className="p-1 bg-white/20 rounded"
                                  >
                                    <Eye className="w-3 h-3 text-white" />
                                  </button>
                                  {(isOwner || isManager()) && (
                                    <button
                                      onClick={() => handleDeleteScreenshot(achievement.id, screenshot.id)}
                                      className="p-1 bg-red-500/80 rounded"
                                    >
                                      <Trash2 className="w-3 h-3 text-white" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Upload Screenshot Button */}
                      {(isOwner || isManager()) && (
                        <div className="mb-3">
                          <label className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors",
                            isDark 
                              ? "border-slate-600 hover:border-slate-500 hover:bg-slate-700/50" 
                              : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                          )}>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleScreenshotUpload(achievement.id, e.target.files[0])}
                              disabled={uploadingScreenshot === achievement.id}
                            />
                            {uploadingScreenshot === achievement.id ? (
                              <div className="animate-spin w-4 h-4 border-2 border-[#331062] border-t-transparent rounded-full" />
                            ) : (
                              <Upload className={cn("w-4 h-4", isDark ? "text-slate-400" : "text-slate-500")} />
                            )}
                            <span className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                              {uploadingScreenshot === achievement.id ? 'Uploading...' : 'Add Screenshot'}
                            </span>
                          </label>
                        </div>
                      )}

                      <div className={cn(
                        "flex items-center gap-4 text-xs pt-2 border-t",
                        isDark ? "border-slate-700 text-slate-500" : "border-slate-100 text-slate-400"
                      )}>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {achievement.designer_name}
                        </span>
                        {achievement.date_achieved && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {achievement.date_achieved}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>

      {/* Stats Summary */}
      {achievements.length > 0 && (
        <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div>
                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>
                  {achievements.length}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Total</p>
              </div>
              <div>
                <p className={cn("text-2xl font-bold text-amber-500")}>
                  {achievements.filter(a => a.category === 'innovation').length}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Innovations</p>
              </div>
              <div>
                <p className={cn("text-2xl font-bold text-indigo-500")}>
                  {achievements.filter(a => a.category === 'kt_session').length}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>KT Sessions</p>
              </div>
              <div>
                <p className={cn("text-2xl font-bold text-purple-500")}>
                  {achievements.filter(a => a.category === 'automation').length}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Automations</p>
              </div>
              <div>
                <p className={cn("text-2xl font-bold text-yellow-500")}>
                  {achievements.filter(a => a.category === 'award').length}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Awards</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Achievements;
